import { Box, Flex } from "@chakra-ui/react";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { useNoticeContext } from "@root/context/notice-context";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import { ROUTE_NAMES } from "@root/constants";
import { useEffect, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EnrollmentList from "../enrollment-list/enrollmentlist";
import { TrendingTitle } from "../kpecDashboard/style";
import ViewAllToggle from "../common/ViewAllToggle";
import useMediaQ from "@root/hooks/useMediaQ";
import { UnauthenticatedNoticeList } from "./UnauthenticatedNoticeList";

interface DashboardAreaProps {
  noOfCourses: number;
  courseCardRef?: RefObject<HTMLDivElement>;
}

export const DashboardArea = ({
  noOfCourses,
  courseCardRef,
}: DashboardAreaProps) => {
  const { fetchFreeCourses, freeCoursesData } = useCourseManagementContext();
  const { fetchPublishNotice, publishNoticeData, publishNoticeLoading } =
    useNoticeContext();
  const { slug } = useOrganizationBranding();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  useEffect(() => {
    if (!slug) return; // Wait until slug is available from the by-domain API
    fetchFreeCourses({ slug, recommended: true });
    fetchPublishNotice("", 1, undefined, slug); // pass slug directly to avoid stale closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return (
    <Flex
      gap={6}
      width="100%"
      align="start"
      flexDirection={isMobile ? "column" : "row"}
    >
      {/* Videos section */}
      <Box flex="1" minW="0" width={isMobile ? "100%" : undefined}>
        <EnrollmentList
          courses={freeCoursesData?.rows}
          courseCardRef={courseCardRef}
        />
      </Box>

      {/* Notices section */}
      <Box flex="1" minW="0" width={isMobile ? "100%" : undefined}>
        <Flex align="center" justify="space-between" width="100%">
          <TrendingTitle alignSelf="flex-start" mb={"12px"}>
            {t("notice.noticesTitle")}
          </TrendingTitle>
          <ViewAllToggle
            expanded={false}
            onToggle={() => navigate(ROUTE_NAMES.UNAUTHENTICATED_NOTICE)}
            showToggle={(publishNoticeData?.length ?? 0) > 0}
          />
        </Flex>
        <UnauthenticatedNoticeList
          notices={publishNoticeData}
          loading={publishNoticeLoading}
          fullHeight={true}
        />
      </Box>
    </Flex>
  );
};
